import { useCallback, useEffect, useRef } from 'react';

import type { ActiveTrainState, JourneySocketData } from '@/types';

import useSocketSubscription from '@/hooks/use-socket-subscription';
import rollbar from '@/lib/rollbar';
import { computePathBounds } from '@/utils/helpers/train-simulation/compute-path-bounds';
import { formatDeviation } from '@/utils/helpers/train-simulation/format-deviation';

interface useProcessSocketDataProps {
  trainStateMapRef: { current: Map<string, ActiveTrainState> };
  onDataUpdate: () => void;
}

export const useProcessSocketData = ({
  trainStateMapRef,
  onDataUpdate,
}: useProcessSocketDataProps) => {
  type Journey = NonNullable<JourneySocketData['journeys']>[number];

  const onDataUpdateRef = useRef(onDataUpdate);

  useEffect(() => {
    onDataUpdateRef.current = onDataUpdate;
  }, [onDataUpdate]);

  const { data: activeJourneyData } =
    useSocketSubscription<JourneySocketData>('active_journeys_for_map');

  const cachedPathDataRef = useRef(
    new Map<
      string,
      {
        path: Journey['path'];
        pathDistanceArray: Float64Array;
        pathBounds: ReturnType<typeof computePathBounds>;
        segmentBearings: Float32Array;
      }
    >()
  );

  const processData = useCallback(
    (socketData: JourneySocketData) => {
      if (!socketData?.activeJourneyIds) {
        return;
      }

      const validIds = new Set(socketData.activeJourneyIds);

      for (const id of trainStateMapRef.current.keys()) {
        if (!validIds.has(id)) {
          trainStateMapRef.current.delete(id);
          cachedPathDataRef.current.delete(id);
        }
      }

      if (socketData.journeys) {
        for (const j of socketData.journeys) {
          if (!j || !j.id) {
            continue;
          }

          try {
            const id = j.id;
            const existing = trainStateMapRef.current.get(id);

            const startT = Date.parse(j.startTime) || 0;
            const endT = Date.parse(j.endTime) || 0;
            const duration = endT - startT;

            let deviationCategory = existing?.deviationCategory;
            let deviationTime = existing?.deviationTime;

            if (
              !existing ||
              j.deviation !== existing.lastDeviation ||
              j.willStart !== existing.willStart
            ) {
              const formatted = formatDeviation(j.deviation, j.willStart);
              deviationCategory = formatted.deviationCategory;
              deviationTime = formatted.deviationTime;
            }

            let pathData = cachedPathDataRef.current.get(id);

            if (j.pathUpdated && j.path && j.pathDistance && j.pathBearings) {
              pathData = {
                path: j.path,
                pathDistanceArray: new Float64Array(j.pathDistance),
                pathBounds: computePathBounds(j.path),
                segmentBearings: new Float32Array(j.pathBearings),
              };

              cachedPathDataRef.current.set(id, pathData);
            }

            if (!pathData) {
              if (existing) {
                trainStateMapRef.current.set(id, {
                  ...existing,
                  duration,
                  startTime: startT,
                  atCurrentStop: j.atCurrentStop,
                  hasActiveDangerousGoods: j.hasActiveDangerousGoods,
                  deviationCategory: deviationCategory!,
                  deviationTime: deviationTime!,
                  journeyFinished: j.journeyFinished,
                  willStart: j.willStart,
                  isOperational: j.isOperational,
                  journeyCategory: j.journeyCategory,
                  lastDeviation: j.deviation,
                  journeyState: j.journeyState,
                } as ActiveTrainState);
              }
              continue;
            }

            trainStateMapRef.current.set(id, {
              id,
              path: pathData.path!,
              pathDistance: pathData.pathDistanceArray,
              pathBounds: pathData.pathBounds,
              segmentBearings: pathData.segmentBearings,
              totalDistance: j.totalDistance,
              duration,
              startTime: startT,
              hasActiveDangerousGoods: j.hasActiveDangerousGoods,
              atCurrentStop: j.atCurrentStop,
              journeyNumber: j.journeyNumber,
              journeyType: j.journeyType || '',
              lineNumber: j.lineNumber,
              deviationCategory: deviationCategory!,
              deviationTime: deviationTime!,
              journeyFinished: j.journeyFinished,
              willStart: j.willStart,
              isOperational: j.isOperational,
              journeyCategory: j.journeyCategory,
              lastDeviation: j.deviation,
              journeyState: j.journeyState,
            } as ActiveTrainState);
          } catch (error) {
            rollbar.error('Socket: Failed to process single train journey', error as Error, {
              journeyId: j?.id,
              journeyNumber: j?.journeyNumber,
            });
          }
        }
      }

      onDataUpdateRef.current();
    },

    [trainStateMapRef]
  );

  useEffect(() => {
    if (activeJourneyData) {
      processData(activeJourneyData);
    }
  }, [activeJourneyData, processData]);
};
